import React, { useState } from 'react'
import { formatCurrency } from 'utils/currencyFormatter'
import { interpolate } from 'utils/i18nUtils'
import { groupPackagesByParticipants, participantsLabel, PARTICIPANTS_LINK_PARAM } from 'utils/participantGroups'

/**
 * Unified package selector for course packages.
 * Displays trial + regular packages with unit price as primary format.
 *
 * Props:
 *   packages       - array of { id, lessonCount, unitPrice, totalPrice, currency, isTrial, durationMinutes,
 *                    participants, perPersonPrice, perPersonSavings }
 *                    Packages for several people are grouped by size behind a toggle;
 *                    prices and savings are only ever compared inside one size.
 *   courseDuration - fallback duration from course (minutes)
 *   selectedId     - currently selected package id
 *   onSelect       - (packageId) => void — called on every click (for state tracking)
 *   onPurchase     - (pkg) => void — if provided, called on click to directly trigger purchase
 *   trialUsed      - boolean, hide trial if already used
 *   colors         - { primary }
 *   translations   - i18n keys
 *   discountPreviews - optional map { [packageId]: { applied, welcome_applied, referral_applied, final_amount } }
 *                      from useDiscountPreviews; shows the student's coupon/referral
 *                      discount per package so they see the deal without clicking.
 */
export default function PackageSelector({ packages, courseDuration, selectedId, onSelect, onPurchase, trialUsed, colors, translations, discountPreviews }) {
  const groups = groupPackagesByParticipants(packages)
  // An invitation link ("?participants=2") opens on that size when the course sells it.
  const [activeSize, setActiveSize] = useState(() => {
    const search = typeof window === 'undefined' ? '' : window.location.search
    const linked = Number(new URLSearchParams(search).get(PARTICIPANTS_LINK_PARAM))
    return groups.some(g => g.participants === linked) ? linked : (groups[0]?.participants || 1)
  })

  if (!packages || packages.length === 0) return null

  const activeGroup = groups.find(g => g.participants === activeSize) || groups[0]
  const tr = (key, fallback, vars) => interpolate(translations?.[key] || fallback, vars)

  // Student coupon / referral discount badge for a package (welcome coupon takes
  // priority for the label since it is consumed first).
  const renderDiscountBadge = (pkg, { light = false } = {}) => {
    const dp = discountPreviews?.[pkg.id]
    if (!dp || !(dp.applied > 0)) return null

    const label = dp.welcome_applied > 0
      ? (translations?.welcomeCouponLabel || 'Welcome coupon')
      : (translations?.referralDiscountLabel || 'Referral discount')
    const isFree = dp.final_amount <= 0

    return (
      <span className="block text-xs font-semibold mt-0.5" style={{ color: light ? 'rgba(255,255,255,0.9)' : '#3366FF' }}>
        🎁 {label} {isFree
          ? (translations?.couponFree || 'First purchase free')
          : `−${formatCurrency(dp.applied, pkg.currency)}`}
      </span>
    )
  }

  const regularPkgs = activeGroup ? activeGroup.packages : []
  const isGroup = (activeGroup?.participants || 1) > 1
  // One trial however many come, as tutoring platforms and agencies handle it.
  // It stays on the group tabs too, with a note, so a family does not read its
  // absence as "no trial for siblings".
  const trialPkg = !trialUsed ? packages.find(p => p.isTrial) : null

  return (
    <div className="space-y-2">
      {/* Group size toggle — only when the course sells more than one size */}
      {groups.length > 1 && (
        <div className="mb-1">
          <p className="text-sm font-medium text-gray-900 mb-2">{tr('participantsQuestion', 'How many people?')}</p>
          <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
            {groups.map(group => {
              const active = group.participants === activeGroup.participants
              return (
                <button
                  key={group.participants}
                  type="button"
                  onClick={() => {
                    setActiveSize(group.participants)
                    // A package left selected in the hidden size must not be bought by mistake.
                    if (selectedId && !group.packages.some(p => p.id === selectedId)) onSelect?.(null)
                  }}
                  className="px-4 py-2.5 min-h-[44px] text-sm cursor-pointer transition-colors"
                  style={active ? { backgroundColor: '#E6F0FF', color: colors?.primary || '#3366FF', fontWeight: 600 } : {}}
                  aria-pressed={active}
                >
                  <span className="whitespace-nowrap">{participantsLabel(group.participants, translations, 'participantsOption')}</span>
                  {group.maxSavings && (
                    <span className="block text-xs text-emerald-600 whitespace-nowrap">
                      {tr('perPersonSavingsShort', 'save %{amount} each', { amount: formatCurrency(group.maxSavings, group.packages[0]?.currency) })}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* Trial package */}
      {trialPkg && (
        <div
          onClick={() => { onSelect(trialPkg.id); onPurchase?.(trialPkg) }}
          className={`flex items-center justify-between px-4 py-3.5 rounded-lg cursor-pointer transition-all ${
            selectedId === trialPkg.id
              ? 'ring-2 ring-offset-1 ring-emerald-400'
              : 'hover:opacity-90'
          }`}
          style={{ background: 'linear-gradient(135deg, #10B981, #34D399)' }}
        >
          <div>
            <span className="font-bold text-white text-lg">
              {trialPkg.totalPrice === 0 ? (translations?.free || 'Free') : formatCurrency(trialPkg.totalPrice, trialPkg.currency)}
            </span>
            <span className="block text-emerald-100 text-sm">
              {trialPkg.durationMinutes || courseDuration}{translations?.trialLesson || 'min trial lesson'}
            </span>
            {isGroup && (
              <span className="block text-emerald-50 text-xs mt-0.5">
                {translations?.trialTogetherNote || 'Come together to the trial too'}
              </span>
            )}
            {renderDiscountBadge(trialPkg, { light: true })}
          </div>
          <svg className="w-5 h-5 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      )}

      {/* Regular packages */}
      {regularPkgs.map((pkg, idx) => {
        const isSelected = selectedId === pkg.id
        // Single-lesson package: the displayed price IS the order total, so the
        // coupon meaningfully changes it — show the original struck through + the
        // discounted price. Multi-lesson cards show a per-lesson unit price, where
        // a flat coupon off the whole order would be misleading struck, so those
        // keep just the discount badge below.
        const dp = discountPreviews?.[pkg.id]
        // Strike the actual ORDER amount (the package total the coupon discounts),
        // not the per-lesson unit price — they can differ even for a 1-lesson pack.
        const origAmount = dp?.original_amount ?? pkg.totalPrice
        const showStrikethrough = pkg.lessonCount === 1 && dp && dp.applied > 0 && dp.final_amount < origAmount
        const baseUnitPrice = regularPkgs[0]?.unitPrice
        const discountPercent = baseUnitPrice && idx > 0
          ? Math.round((1 - pkg.unitPrice / baseUnitPrice) * 100)
          : 0
        const showPopular = regularPkgs.length > 1 && idx === (regularPkgs.length === 2 ? regularPkgs.length - 1 : regularPkgs.length - 2)
        // Calculate total savings compared to buying single lessons
        const savingsAmount = baseUnitPrice && idx > 0
          ? (baseUnitPrice * pkg.lessonCount) - pkg.totalPrice
          : 0

        return (
          <div
            key={pkg.id}
            onClick={() => { onSelect(pkg.id); onPurchase?.(pkg) }}
            className={`flex items-center justify-between px-4 py-3 rounded-lg cursor-pointer transition-all border-2 ${
              isSelected
                ? 'bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            style={isSelected ? { borderColor: colors?.primary || '#3366FF' } : {}}
          >
            <div>
              {showStrikethrough ? (
                <span className="font-bold text-lg">
                  <span className="line-through text-gray-400 mr-2">{formatCurrency(origAmount, pkg.currency)}</span>
                  <span style={{ color: '#3366FF' }}>{formatCurrency(dp.final_amount, pkg.currency)}</span>
                </span>
              ) : (
                <span className="font-bold text-lg text-gray-900">
                  {formatCurrency(pkg.unitPrice, pkg.currency)}
                </span>
              )}
              <span className="block text-sm text-gray-500">
                {pkg.lessonCount} {translations?.lessonCount || 'lesson(s)'} {pkg.durationMinutes || courseDuration} {translations?.min || 'min'}{translations?.lessonUnit || ''}
                {isGroup && tr('groupTotal', ', per lesson for %{count} people', { count: pkg.participants })}
              </span>
              {isGroup && pkg.perPersonPrice != null && (
                <span className="block text-xs font-medium text-emerald-600 mt-0.5">
                  {pkg.perPersonSavings
                    ? tr('perPersonVsSolo', '%{amount} each, %{savings} less than one-on-one', {
                        amount: formatCurrency(pkg.perPersonPrice, pkg.currency),
                        savings: formatCurrency(pkg.perPersonSavings, pkg.currency)
                      })
                    : tr('perPersonPrice', '%{amount} each', { amount: formatCurrency(pkg.perPersonPrice, pkg.currency) })}
                </span>
              )}
              {savingsAmount > 0 && (
                <span className="block text-xs font-medium text-emerald-600 mt-0.5">
                  {(translations?.savingsAmount || 'Save %{amount}').replace('%{amount}', formatCurrency(savingsAmount, pkg.currency))}
                </span>
              )}
              {renderDiscountBadge(pkg)}
              {(showPopular || discountPercent > 0) && (
                <div className="flex items-center gap-1.5 mt-1.5">
                  {showPopular && (
                    <span
                      className="px-2 py-0.5 text-xs font-semibold rounded-full text-white"
                      style={{ backgroundColor: colors?.primary || '#3366FF' }}
                    >
                      {translations?.popularBadge || 'Popular'}
                    </span>
                  )}
                  {discountPercent > 0 && (
                    <span
                      className="px-2 py-0.5 text-xs font-semibold rounded border"
                      style={{ color: colors?.primary || '#3366FF', borderColor: colors?.primary || '#3366FF' }}
                    >
                      {(translations?.discountOff || '%{percent}% off').replace('%{percent}', discountPercent)}
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center shrink-0 ml-3">
              <span className="text-gray-400">›</span>
            </div>
          </div>
        )
      })}
    </div>
  )
}
